import { useCallback, useEffect, useRef } from 'react';

// 40 iterations x 30s = 20 min hard cap, aligned with UW's classification-pending staleness cutoff.
export const CLASSIFICATION_POLL_INTERVAL_MS = 30000;
export const MAX_CLASSIFICATION_POLL_ITERATIONS = 40;

/**
 * Bounded poll for the Pass-2 (post-classification) pending window: while `pending` is true,
 * re-invokes `refetch` every CLASSIFICATION_POLL_INTERVAL_MS, up to MAX_CLASSIFICATION_POLL_ITERATIONS
 * per org. On the final tick the poll still fires `refetch` (observes the backend one last time)
 * before calling `onCapExhausted` — the consumer must force its local pending flag to false there so
 * a stuck/SKIPPED org falls back to "-" by construction, independent of backend timing (A-08/A-09).
 *
 * The iteration budget lives in a ref keyed to the current org: it survives `pending` flip-flops
 * (e.g. a legitimate re-pend from a second upload) and only resets on a genuine org change — a
 * stale response flipping `pending` can't grant a fresh 20-min budget. Callers should additionally
 * guard fetch responses with `useLatestRequestGuard` and consult `isCapReached()` before trusting a
 * response's own pending flag, in case a late response for a capped org arrives after `onCapExhausted`.
 */
export function useBoundedClassificationPoll(orgId: string, pending: boolean, refetch: () => void, onCapExhausted: () => void) {
    const iterationsRef = useRef(0);
    const orgIdRef = useRef(orgId);
    const refetchRef = useRef(refetch);
    const onCapExhaustedRef = useRef(onCapExhausted);

    refetchRef.current = refetch;
    onCapExhaustedRef.current = onCapExhausted;

    useEffect(() => {
        if (orgIdRef.current !== orgId) {
            orgIdRef.current = orgId;
            iterationsRef.current = 0;
        }
    }, [orgId]);

    useEffect(() => {
        if (!pending || iterationsRef.current >= MAX_CLASSIFICATION_POLL_ITERATIONS) return;

        const intervalId = setInterval(() => {
            refetchRef.current();
            iterationsRef.current += 1;
            if (iterationsRef.current >= MAX_CLASSIFICATION_POLL_ITERATIONS) {
                clearInterval(intervalId);
                onCapExhaustedRef.current();
            }
        }, CLASSIFICATION_POLL_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [pending, orgId]);

    const isCapReached = useCallback(() => iterationsRef.current >= MAX_CLASSIFICATION_POLL_ITERATIONS, []);

    return { isCapReached };
}
